import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { INDUSTRIES } from '../data/constants';
import { submitIntelligenceReviewLead } from '../services/leadService';
import CustomSelect from '../components/common/CustomSelect';

const MONITORING_PLATFORMS = ['Meta', 'Google', 'TikTok', 'LinkedIn'];
const AD_SPEND_RANGES = ['Below ₦500k', '₦500k - ₦1.5M', '₦1.5M - ₦5M', '₦5M - ₦15M', 'Above ₦15M'];
const CONTACT_METHODS = ['Email', 'WhatsApp', 'Phone Call'];

export default function RequestReview() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    job_title: '',
    work_email: '',
    telephone: '',
    website: '',
    industry: '',
    country: '',
    primary_market: '',
    competitors: '',
    competitor_websites: '',
    monitoring_platforms: [],
    competitive_concern: '',
    current_advertising_platforms: [],
    campaign_objective: '',
    marketing_challenge: '',
    desired_result: '',
    monthly_advertising_range: '',
    preferred_contact_method: '',
    consent: false,
    referral_source: '',
    preferred_start_date: null
  });

  const updateField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));
  
  const toggleArrayItem = (name, item) => {
    setFormData(prev => {
      const current = prev[name];
      const updated = current.includes(item) ? current.filter(i => i !== item) : [...current, item];
      return { ...prev, [name]: updated };
    });
  };

  const next = () => setStep(s => s + 1);
  const back = () => setStep(s => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await submitIntelligenceReviewLead(formData);
      setStep(6);
    } catch (err) {
      // During development: Show exact error
      setError(`Submission failed: ${err.message || 'Unknown Error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black pt-32 pb-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-white">Request an Intelligence Review</h1>
          {step < 6 && (
            <div className="flex items-center gap-6">
              <div className="flex-grow bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                <motion.div initial={false} animate={{ width: `${(step / 5) * 100}%` }} className="bg-blue-600 h-full" />
              </div>
              <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase shrink-0">Step {step} of 5</span>
            </div>
          )}
        </div>

        <div className="bg-zinc-950 border border-zinc-900 p-8 md:p-16 rounded-[3rem] relative overflow-visible">
          {error && (
            <div className="mb-8 p-4 bg-red-900/20 text-red-400 rounded-xl text-xs border border-red-900/30 font-medium">
              {error}
            </div>
          )}
          
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                <h2 className="text-2xl font-serif font-bold mb-8 text-white">Organisation Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Company Name *</label>
                    <input type="text" className="input-field" value={formData.company_name} onChange={e => updateField('company_name', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Website *</label>
                    <input type="text" className="input-field" value={formData.website} onChange={e => updateField('website', e.target.value)} placeholder="e.g. maxmarkagency.com" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-visible">
                  <CustomSelect label="Industry" options={INDUSTRIES} value={formData.industry} onChange={val => updateField('industry', val)} placeholder="Select Industry" required />
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Country *</label>
                    <input type="text" className="input-field" value={formData.country} onChange={e => updateField('country', e.target.value)} required />
                  </div>
                </div>
                <button onClick={next} className="w-full bg-blue-600 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs mt-6 transition-all hover:bg-blue-700">Next: Market & Competitors</button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                <h2 className="text-2xl font-serif font-bold mb-8 text-white">Market & Competitors</h2>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Primary Market *</label>
                  <input type="text" placeholder="e.g. Nigeria, West Africa" className="input-field" value={formData.primary_market} onChange={e => updateField('primary_market', e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Competitors to monitor * (One per line)</label>
                  <textarea rows="3" className="input-field" value={formData.competitors} onChange={e => updateField('competitors', e.target.value)} required></textarea>
                </div>
                <div className="space-y-4">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Monitoring Platforms *</label>
                  <div className="flex flex-wrap gap-3">
                    {MONITORING_PLATFORMS.map(p => (
                      <button key={p} type="button" onClick={() => toggleArrayItem('monitoring_platforms', p)} className={`px-6 py-3 rounded-xl border text-[10px] font-bold uppercase tracking-widest transition-all ${formData.monitoring_platforms.includes(p) ? 'bg-white text-black border-white' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}> {p} </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Main competitive concern *</label>
                  <textarea rows="3" className="input-field" value={formData.competitive_concern} onChange={e => updateField('competitive_concern', e.target.value)} required></textarea>
                </div>
                <div className="flex gap-4 pt-6">
                  <button type="button" onClick={back} className="flex-1 border border-zinc-800 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs">Back</button>
                  <button type="button" onClick={next} className="flex-1 bg-blue-600 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs transition-all hover:bg-blue-700">Next Step</button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6 overflow-visible">
                <h2 className="text-2xl font-serif font-bold mb-8 text-white">Advertising & Goals</h2>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Current Campaign Objective *</label>
                  <input type="text" className="input-field" value={formData.campaign_objective} onChange={e => updateField('campaign_objective', e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Main marketing challenge *</label>
                  <textarea rows="3" className="input-field" value={formData.marketing_challenge} onChange={e => updateField('marketing_challenge', e.target.value)} required></textarea>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Desired result from this review *</label>
                  <textarea rows="3" className="input-field" value={formData.desired_result} onChange={e => updateField('desired_result', e.target.value)} required></textarea>
                </div>
                <CustomSelect label="Monthly Ad Spend" options={AD_SPEND_RANGES} value={formData.monthly_advertising_range} onChange={val => updateField('monthly_advertising_range', val)} placeholder="Select range (Optional)" />
                <div className="flex gap-4 pt-6">
                  <button type="button" onClick={back} className="flex-1 border border-zinc-800 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs">Back</button>
                  <button type="button" onClick={next} className="flex-1 bg-blue-600 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs transition-all hover:bg-blue-700">Next Step</button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6 overflow-visible">
                <h2 className="text-2xl font-serif font-bold mb-8 text-white">Contact & Consent</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Full Name *</label>
                    <input type="text" className="input-field" value={formData.full_name} onChange={e => updateField('full_name', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Job Title *</label>
                    <input type="text" className="input-field" value={formData.job_title} onChange={e => updateField('job_title', e.target.value)} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Work Email *</label>
                  <input type="email" className="input-field" value={formData.work_email} onChange={e => updateField('work_email', e.target.value)} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-visible">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase ml-1">Telephone/WhatsApp *</label>
                    <input type="tel" className="input-field" value={formData.telephone} onChange={e => updateField('telephone', e.target.value)} required />
                  </div>
                  <CustomSelect label="Preferred Contact Method" options={CONTACT_METHODS} value={formData.preferred_contact_method} onChange={val => updateField('preferred_contact_method', val)} placeholder="Select method" required />
                </div>
                <div className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl mt-6 border border-zinc-800">
                  <input type="checkbox" id="review-consent" className="mt-1" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} required />
                  <label htmlFor="review-consent" className="text-xs text-zinc-500 leading-relaxed cursor-pointer select-none"> I agree to the processing of my data according to the Maxmark Privacy Policy. </label>
                </div>
                <div className="flex gap-4 pt-6">
                  <button type="button" onClick={back} className="flex-1 border border-zinc-800 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs">Back</button>
                  <button type="button" onClick={next} className="flex-1 bg-blue-600 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs transition-all hover:bg-blue-700">Review Request</button>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-8">
                <h2 className="text-2xl font-serif font-bold mb-8 text-white">Final Review</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-zinc-900/50 p-6 rounded-2xl space-y-3 border border-zinc-800">
                    <h3 className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Organisation</h3>
                    <p className="text-sm text-white font-medium">{formData.company_name}</p>
                    <p className="text-xs text-zinc-500">{formData.website}</p>
                  </div>
                  <div className="bg-zinc-900/50 p-6 rounded-2xl space-y-3 border border-zinc-800">
                    <h3 className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Contact</h3>
                    <p className="text-sm text-white font-medium">{formData.full_name}</p>
                    <p className="text-xs text-zinc-500">{formData.work_email}</p>
                  </div>
                </div>
                <div className="flex gap-4 pt-6">
                  <button type="button" onClick={back} className="flex-1 border border-zinc-800 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs">Edit Details</button>
                  <button type="button" onClick={handleSubmit} disabled={loading} className="flex-1 bg-blue-600 text-white py-5 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-blue-700 transition-all disabled:opacity-50"> {loading ? 'Submitting...' : 'Confirm & Submit'} </button>
                </div>
              </motion.div>
            )}

            {step === 6 && (
              <motion.div key="s6" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-blue-600/20">
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-4xl font-serif font-bold mb-6 text-white">Review Requested</h2>
                <p className="text-xl text-zinc-500 mb-12">Our strategists have received your request and will contact you shortly.</p>
                <Link to="/" className="inline-block bg-white text-black px-12 py-4 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors">Return Home</Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}